import React, { useState, useMemo } from 'react';
import { ChevronLeft, ChevronRight, Filter, Check, X, Eye } from 'lucide-react';

const colors = {
  primary: '#2E8B98',
  success: '#62BB46',
  warning: '#F26522',
  danger: '#DC3545',
  background: '#F8F9FA',
  text: '#212529',
  lightText: '#6C757D',
  shadow: '5px 5px 10px rgba(186,199,216,0.45), -5px -5px 10px rgba(255,255,255,0.8)',
  insetShadow: 'inset 3px 3px 6px rgba(186,199,216,0.45), inset -3px -3px 6px rgba(255,255,255,0.8)',
};

const LEAVE_TYPES = {
  annual: { label: 'Annual', color: colors.primary },
  sick: { label: 'Sick', color: colors.warning },
  personal: { label: 'Personal', color: colors.success },
};

const STATUS_COLORS = {
  pending: colors.warning,
  approved: colors.success,
  rejected: colors.danger,
  cancelled: '#6C757D',
};

function generateDemoLeaveData(personnel) {
  const leaves = [];
  const leaveTypes = Object.keys(LEAVE_TYPES);
  const statuses = ['pending', 'approved', 'rejected', 'cancelled'];
  const today = new Date(2026, 2, 25); // March 25, 2026

  personnel.forEach((emp, idx) => {
    for (let i = 0; i < 3; i++) {
      const daysOffset = Math.floor(Math.random() * 60) - 30;
      const duration = Math.floor(Math.random() * 10) + 1;
      const startDate = new Date(today.getTime() + daysOffset * 24 * 60 * 60 * 1000);
      const endDate = new Date(startDate.getTime() + duration * 24 * 60 * 60 * 1000);

      leaves.push({
        id: `leave-${idx}-${i}`,
        employeeId: emp.id,
        employeeName: emp.name,
        employeeAvatar: emp.avatar || `${emp.name.split(' ').map(n => n[0]).join('')}`,
        type: leaveTypes[Math.floor(Math.random() * leaveTypes.length)],
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        days: duration,
        status: statuses[Math.floor(Math.random() * statuses.length)],
        reason: ['Personal', 'Medical', 'Vacation', 'Family', 'Other'][Math.floor(Math.random() * 5)],
      });
    }
  });

  return leaves;
}

function KpiCard({ title, value, icon: Icon, color, bgColor }) {
  const cardStyle = {
    flex: 1,
    minWidth: '200px',
    padding: '20px',
    borderRadius: '12px',
    background: colors.background,
    boxShadow: colors.shadow,
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  };

  const titleStyle = {
    fontSize: '14px',
    fontWeight: '600',
    color: colors.lightText,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  };

  const valueStyle = {
    fontSize: '28px',
    fontWeight: 'bold',
    color: color,
  };

  const iconStyle = {
    width: '24px',
    height: '24px',
    color: color,
  };

  return (
    <div style={cardStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={titleStyle}>{title}</div>
          <div style={valueStyle}>{value}</div>
        </div>
        <Icon style={iconStyle} strokeWidth={2} />
      </div>
    </div>
  );
}

function LeaveCalendar({ leaves, selectedDate, onDateSelect }) {
  const [currentDate, setCurrentDate] = useState(new Date(2026, 2, 1));

  const getDaysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const getFirstDayOfMonth = (date) => new Date(date.getFullYear(), date.getMonth(), 1).getDay();

  const daysInMonth = getDaysInMonth(currentDate);
  const firstDay = getFirstDayOfMonth(currentDate);
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);

  const getLeaveDotsForDay = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dayLeaves = leaves.filter(l => {
      const start = l.startDate;
      const end = l.endDate;
      return dateStr >= start && dateStr <= end;
    });

    const typeMap = {};
    dayLeaves.forEach(l => {
      if (!typeMap[l.type]) typeMap[l.type] = 0;
      typeMap[l.type]++;
    });
    return typeMap;
  };

  const monthName = currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });

  const containerStyle = {
    padding: '20px',
    borderRadius: '12px',
    background: colors.background,
    boxShadow: colors.shadow,
    marginBottom: '20px',
  };

  const headerStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  };

  const buttonStyle = {
    padding: '8px 12px',
    borderRadius: '8px',
    border: 'none',
    background: colors.background,
    boxShadow: colors.shadow,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: colors.text,
    fontSize: '14px',
    fontWeight: '600',
    transition: 'all 0.2s',
  };

  const gridStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(7, 1fr)',
    gap: '8px',
    marginBottom: '20px',
  };

  const dayHeaderStyle = {
    textAlign: 'center',
    fontSize: '12px',
    fontWeight: 'bold',
    color: colors.lightText,
    padding: '8px 0',
    textTransform: 'uppercase',
  };

  const emptyDayStyle = {
    aspectRatio: '1',
    background: '#FFFFFF',
    borderRadius: '8px',
  };

  const dayStyle = (day) => {
    const isSelected = selectedDate && selectedDate.getDate() === day && selectedDate.getMonth() === currentDate.getMonth();
    return {
      aspectRatio: '1',
      padding: '8px',
      borderRadius: '8px',
      background: isSelected ? colors.primary : colors.background,
      boxShadow: colors.shadow,
      border: isSelected ? `2px solid ${colors.primary}` : 'none',
      cursor: 'pointer',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      transition: 'all 0.2s',
      color: isSelected ? '#FFFFFF' : colors.text,
      fontWeight: isSelected ? 'bold' : '600',
      fontSize: '14px',
    };
  };

  const dotStyle = (color) => ({
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    background: color,
  });

  const daysDisplay = Array(firstDay).fill(null).concat(days);

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h3 style={{ margin: 0, color: colors.text, fontSize: '16px', fontWeight: '700' }}>
          {monthName}
        </h3>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button
            style={buttonStyle}
            onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
          >
            <ChevronLeft size={16} />
          </button>
          <button
            style={buttonStyle}
            onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      <div style={gridStyle}>
        {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
          <div key={day} style={dayHeaderStyle}>
            {day}
          </div>
        ))}

        {daysDisplay.map((day, idx) =>
          day === null ? (
            <div key={`empty-${idx}`} style={emptyDayStyle} />
          ) : (
            <div
              key={day}
              style={dayStyle(day)}
              onClick={() => onDateSelect(new Date(currentDate.getFullYear(), currentDate.getMonth(), day))}
            >
              <div>{day}</div>
              <div style={{ display: 'flex', gap: '2px', marginTop: '2px', flexWrap: 'wrap', justifyContent: 'center' }}>
                {Object.entries(getLeaveDotsForDay(day)).slice(0, 3).map(([type]) => (
                  <div key={type} style={dotStyle(LEAVE_TYPES[type].color)} />
                ))}
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}

function LeaveRequestsTable({ leaves, personnel, user, statusFilter, onStatusFilterChange }) {
  // RBAC UI-keying: approve/reject is the personnel:update handler's perm.
  // Dropped the role clause (board_of_directors does NOT hold personnel:update →
  // its handler would deny); admin/sysadmin hold personnel:update so keep it.
  const canApproveReject = !!(user?.permissions && user.permissions.includes('personnel:update'));
  const filteredLeaves = statusFilter === 'all' ? leaves : leaves.filter(l => l.status === statusFilter);

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: '20px',
  };

  const thStyle = {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '700',
    color: colors.lightText,
    textTransform: 'uppercase',
    borderBottom: `2px solid ${colors.primary}`,
    backgroundColor: colors.background,
  };

  const tdStyle = {
    padding: '12px 16px',
    borderBottom: `1px solid #E9ECEF`,
    fontSize: '14px',
    color: colors.text,
  };

  const badgeStyle = (status) => ({
    display: 'inline-block',
    padding: '4px 10px',
    borderRadius: '6px',
    fontSize: '12px',
    fontWeight: '600',
    backgroundColor: STATUS_COLORS[status] + '20',
    color: STATUS_COLORS[status],
  });

  const rowStyle = {
    backgroundColor: '#FFFFFF',
    boxShadow: colors.shadow,
    marginBottom: '8px',
    borderRadius: '8px',
    overflow: 'hidden',
  };

  const containerStyle = {
    padding: '20px',
    borderRadius: '12px',
    background: colors.background,
    boxShadow: colors.shadow,
  };

  const filterStyle = {
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  };

  const selectStyle = {
    padding: '8px 12px',
    borderRadius: '8px',
    border: 'none',
    background: '#FFFFFF',
    boxShadow: colors.shadow,
    color: colors.text,
    cursor: 'pointer',
    fontSize: '14px',
  };

  return (
    <div style={containerStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ margin: 0, color: colors.text, fontSize: '16px', fontWeight: '700' }}>
          Leave Requests
        </h3>
        <div style={filterStyle}>
          <Filter size={16} color={colors.lightText} />
          <select
            style={selectStyle}
            value={statusFilter}
            onChange={(e) => onStatusFilterChange(e.target.value)}
          >
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      {filteredLeaves.length === 0 ? (
        <div style={{ padding: '40px 20px', textAlign: 'center', color: colors.lightText }}>
          No leave requests found.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {filteredLeaves.map(leave => (
            <div key={leave.id} style={rowStyle}>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ display: 'none' }}>
                    <th style={thStyle}>Employee</th>
                    <th style={thStyle}>Type</th>
                    <th style={thStyle}>Start Date</th>
                    <th style={thStyle}>End Date</th>
                    <th style={thStyle}>Days</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '12px', padding: '12px 16px' }}>
                    <td style={{ ...tdStyle, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <div
                        style={{
                          width: '32px',
                          height: '32px',
                          borderRadius: '50%',
                          background: colors.primary,
                          color: '#FFFFFF',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        {leave.employeeAvatar}
                      </div>
                      <div>
                        <div style={{ fontWeight: '600' }}>{leave.employeeName}</div>
                        <div style={{ fontSize: '12px', color: colors.lightText }}>{leave.reason}</div>
                      </div>
                    </td>
                    <td style={tdStyle}>
                      <span
                        style={{
                          padding: '4px 8px',
                          borderRadius: '6px',
                          backgroundColor: LEAVE_TYPES[leave.type].color + '20',
                          color: LEAVE_TYPES[leave.type].color,
                          fontSize: '12px',
                          fontWeight: '600',
                        }}
                      >
                        {LEAVE_TYPES[leave.type].label}
                      </span>
                    </td>
                    <td style={tdStyle}>{leave.startDate}</td>
                    <td style={tdStyle}>{leave.endDate}</td>
                    <td style={tdStyle}>
                      <strong>{leave.days}</strong> days
                    </td>
                    <td style={tdStyle}>
                      <span style={badgeStyle(leave.status)}>
                        {leave.status.charAt(0).toUpperCase() + leave.status.slice(1)}
                      </span>
                    </td>
                    <td style={{ ...tdStyle, display: 'flex', gap: '8px' }}>
                      {canApproveReject && leave.status === 'pending' && (
                        <>
                          <button
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: 'none',
                              background: colors.success,
                              color: '#FFFFFF',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              transition: 'all 0.2s',
                            }}
                          >
                            <Check size={14} />
                            Approve
                          </button>
                          <button
                            style={{
                              padding: '6px 10px',
                              borderRadius: '6px',
                              border: 'none',
                              background: colors.danger,
                              color: '#FFFFFF',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              fontSize: '12px',
                              fontWeight: '600',
                              transition: 'all 0.2s',
                            }}
                          >
                            <X size={14} />
                            Reject
                          </button>
                        </>
                      )}
                      {!canApproveReject && (
                        <button
                          style={{
                            padding: '6px 10px',
                            borderRadius: '6px',
                            border: 'none',
                            background: colors.primary,
                            color: '#FFFFFF',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '4px',
                            fontSize: '12px',
                            fontWeight: '600',
                          }}
                        >
                          <Eye size={14} />
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function BalanceOverview({ personnel }) {
  const balances = personnel.map(emp => ({
    id: emp.id,
    name: emp.name,
    annualUsed: Math.floor(Math.random() * 15),
    annualTotal: 20,
    sickUsed: Math.floor(Math.random() * 8),
    sickTotal: 10,
    personalUsed: Math.floor(Math.random() * 5),
    personalTotal: 5,
  }));

  const getColor = (remaining, total) => {
    const percentage = remaining / total;
    if (percentage > 0.5) return colors.success;
    if (percentage > 0.25) return colors.warning;
    return colors.danger;
  };

  const ProgressBar = ({ used, total }) => {
    const remaining = total - used;
    const percentage = (used / total) * 100;
    return (
      <div
        style={{
          width: '100%',
          height: '6px',
          borderRadius: '3px',
          background: '#E9ECEF',
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${percentage}%`,
            background: getColor(remaining, total),
            transition: 'width 0.3s',
          }}
        />
      </div>
    );
  };

  const containerStyle = {
    padding: '20px',
    borderRadius: '12px',
    background: colors.background,
    boxShadow: colors.shadow,
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
  };

  const thStyle = {
    padding: '12px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: '700',
    color: colors.lightText,
    textTransform: 'uppercase',
    borderBottom: `2px solid ${colors.primary}`,
  };

  const tdStyle = {
    padding: '12px 16px',
    borderBottom: `1px solid #E9ECEF`,
    fontSize: '14px',
    color: colors.text,
  };

  return (
    <div style={containerStyle}>
      <h3 style={{ margin: '0 0 20px 0', color: colors.text, fontSize: '16px', fontWeight: '700' }}>
        Leave Balance Overview
      </h3>

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Employee</th>
            <th style={thStyle}>Annual</th>
            <th style={thStyle}>Sick Leave</th>
            <th style={thStyle}>Personal</th>
            <th style={thStyle}>Total Remaining</th>
          </tr>
        </thead>
        <tbody>
          {balances.map(balance => {
            const annualRemaining = balance.annualTotal - balance.annualUsed;
            const sickRemaining = balance.sickTotal - balance.sickUsed;
            const personalRemaining = balance.personalTotal - balance.personalUsed;
            const totalRemaining = annualRemaining + sickRemaining + personalRemaining;

            return (
              <tr key={balance.id} style={{ backgroundColor: '#FFFFFF', boxShadow: colors.shadow, marginBottom: '8px' }}>
                <td style={tdStyle}>
                  <strong>{balance.name}</strong>
                </td>
                <td style={tdStyle}>
                  <div style={{ marginBottom: '4px', fontSize: '12px', color: colors.lightText }}>
                    {balance.annualUsed}/{balance.annualTotal} days
                  </div>
                  <ProgressBar used={balance.annualUsed} total={balance.annualTotal} />
                </td>
                <td style={tdStyle}>
                  <div style={{ marginBottom: '4px', fontSize: '12px', color: colors.lightText }}>
                    {balance.sickUsed}/{balance.sickTotal} days
                  </div>
                  <ProgressBar used={balance.sickUsed} total={balance.sickTotal} />
                </td>
                <td style={tdStyle}>
                  <div style={{ marginBottom: '4px', fontSize: '12px', color: colors.lightText }}>
                    {balance.personalUsed}/{balance.personalTotal} days
                  </div>
                  <ProgressBar used={balance.personalUsed} total={balance.personalTotal} />
                </td>
                <td style={tdStyle}>
                  <div
                    style={{
                      fontWeight: 'bold',
                      color: getColor(totalRemaining, balance.annualTotal + balance.sickTotal + balance.personalTotal),
                      fontSize: '16px',
                    }}
                  >
                    {totalRemaining} days
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export default function LeaveTab({ personnel, user }) {
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState(null);

  const leaves = useMemo(() => generateDemoLeaveData(personnel), [personnel]);

  const kpiData = useMemo(() => {
    const pending = leaves.filter(l => l.status === 'pending').length;
    const approved = leaves.filter(l => l.status === 'approved' && l.startDate.startsWith('2026-03')).length;
    const rejected = leaves.filter(l => l.status === 'rejected').length;
    const totalDays = leaves.reduce((sum, l) => sum + (l.status === 'approved' ? l.days : 0), 0);

    return {
      pending,
      approved,
      rejected,
      avgBalance: Math.round(totalDays / personnel.length),
    };
  }, [leaves, personnel]);

  const containerStyle = {
    padding: '0',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  };

  const kpiRowStyle = {
    display: 'flex',
    gap: '16px',
    flexWrap: 'wrap',
  };

  return (
    <div style={containerStyle}>
      <div style={kpiRowStyle}>
        <KpiCard
          title="Pending Requests"
          value={kpiData.pending}
          color={colors.warning}
          icon={() => null}
        />
        <KpiCard
          title="Approved This Month"
          value={kpiData.approved}
          color={colors.success}
          icon={() => null}
        />
        <KpiCard
          title="Rejected"
          value={kpiData.rejected}
          color={colors.danger}
          icon={() => null}
        />
        <KpiCard
          title="Avg Balance"
          value={`${kpiData.avgBalance}d`}
          color={colors.primary}
          icon={() => null}
        />
      </div>

      <LeaveCalendar leaves={leaves} selectedDate={selectedDate} onDateSelect={setSelectedDate} />

      <LeaveRequestsTable
        leaves={leaves}
        personnel={personnel}
        user={user}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
      />

      <BalanceOverview personnel={personnel} />
    </div>
  );
}
